import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { STEPS, stepDef, stepIndex, type StepId } from '@/lib/campaignSteps';

/** The step's number, name and one-line hint, so every step opens by saying what it is for. */
export function StepHeader({ step }: { step: StepId }) {
  const { t } = useTranslation();
  const def = stepDef(step);
  return (
    <div className="mb-5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('campaigns.step_of', 'Step {{current}} of {{total}}', { current: stepIndex(step) + 1, total: STEPS.length })}
      </p>
      <h2 className="mt-1 text-xl font-semibold">{t(def.labelKey, def.label)}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t(def.hintKey, def.hint)}</p>
    </div>
  );
}

interface StepFooterProps {
  /** Omitted on the first step, where there is nowhere to go back to. */
  onBack?: () => void;
  backLabel?: string;
  /** The forward action. Defaults to a Next button; the launch step passes its own. */
  primary?: ReactNode;
  onNext?: () => void;
  nextDisabled?: boolean;
  /** Said beside a disabled Next, so a locked button is never a dead end. */
  hint?: ReactNode;
}

export function StepFooter({ onBack, backLabel, primary, onNext, nextDisabled, hint }: StepFooterProps) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
      <div>
        {onBack && (
          <Button type="button" variant="ghost" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            {backLabel || t('campaigns.step_back', 'Back')}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3">
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        {primary ?? (
          <Button type="button" onClick={onNext} disabled={nextDisabled} className="gap-2">
            {t('campaigns.step_next', 'Next')}
            <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Button>
        )}
      </div>
    </div>
  );
}
